import React, { useEffect, useState } from 'react';
import truncate from 'lodash/truncate';
import classNames from 'classnames';
import axios from 'axios';

import { FormattedMessage } from '../../../util/reactIntl';
import { richText } from '../../../util/richText';
import { ensureUser, ensureCurrentUser } from '../../../util/data';
import { apiBaseUrl } from '../../../util/api';
import { propTypes } from '../../../util/types';

import { AvatarLarge, NamedLink, InlineTextButton } from '../../../components';

import css from './UserCard.module.css';
// import verifySeller from '../../../assets/verifyseller.png';

import { ReactComponent as VerifySeller } from '../../../assets/svg/sellerVerifiedBadge.svg';
import { ReactComponent as ChatIconNew } from '../../../assets/svg/chat-icon-new.svg';

// Approximated collapsed size so that there are ~three lines of text
// in the desktop layout in the author section of the ListingPage.
const BIO_COLLAPSED_LENGTH = 170;
const MIN_LENGTH_FOR_LONG_WORDS = 20;

const truncated = s => {
  return truncate(s, {
    length: BIO_COLLAPSED_LENGTH,

    // Allow truncated text end only in specific characters. This will
    // make the truncated text shorter than the length if the original
    // text has to be shortened and the substring ends in a separator.
    //
    // This ensures that the final text doesn't get cut in the middle
    // of a word.
    separator: /\s|,|\.|:|;/,
    omission: '…',
  });
};

const ExpandableBio = props => {
  const [expand, setExpand] = useState(false);
  const { className, bio, user } = props;
  const bioWithLinks = richText(bio, {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
  });
  const truncatedBio = richText(truncated(bio), {
    linkify: true,
    longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS,
    longWordClass: css.longWord,
    breakChars: '/',
  });

  const handleShowMoreClick = () => {
    setExpand(true);
  };
  const showMore = (
    <InlineTextButton rootClassName={css.showMore} onClick={handleShowMoreClick}>
      <FormattedMessage id="UserCard.showFullBioLink" />
    </InlineTextButton>
  );
  return (
    <p className={className}>
      {expand ? bioWithLinks : truncatedBio}
      {bio.length >= BIO_COLLAPSED_LENGTH && !expand ? showMore : null}
    </p>
  );
};

/**
 * The UserCard component.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {propTypes.user | propTypes.currentUser} props.user - The user
 * @param {propTypes.currentUser} props.currentUser - The current user
 * @param {function} props.onContactUser - The on contact user function
 * @param {boolean} [props.showContact] - Whether to show the contact user button
 * @returns {JSX.Element} user card component
 */
const UserCard = props => {
  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const {
    rootClassName,
    className,
    user,
    currentUser,
    onContactUser,
    showContact = true,
    authorRatingCont,
    storeName,
    verifiedMail,
  } = props;

  const userIsCurrentUser = user && user.type === 'currentUser';
  const ensuredUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const isCurrentUser =
    ensuredUser.id && ensuredCurrentUser.id && ensuredUser.id.uuid === ensuredCurrentUser.id.uuid;

  const userId = ensuredUser?.id?.uuid;

  const [isEmailVerified, setIsEmailVerified] = useState(
    isCurrentUser
      ? !!(ensuredCurrentUser?.attributes?.emailVerified ?? ensuredUser?.attributes?.emailVerified)
      : typeof verifiedMail === 'boolean'
      ? verifiedMail
      : !!ensuredUser?.attributes?.emailVerified
  );

  const [sellerRating, setSellerRating] = useState(
    ensuredUser?.attributes?.profile?.publicData?.rating ||
    ensuredUser?.attributes?.profile?.metadata?.rating ||
    ensuredUser?.attributes?.profile?.publicData?.userRating ||
    null
  );
  const [sellerRatingCount, setSellerRatingCount] = useState(
    ensuredUser?.attributes?.profile?.publicData?.ratingCount ||
    ensuredUser?.attributes?.profile?.metadata?.ratingCount ||
    ensuredUser?.attributes?.profile?.publicData?.userRatingCount ||
    null
  );
  const [sellerLastSeen, setSellerLastSeen] = useState(
    ensuredUser?.attributes?.profile?.metadata?.lastSeen || null
  );

  useEffect(() => {
    if (isCurrentUser) {
      setIsEmailVerified(
        !!(ensuredCurrentUser?.attributes?.emailVerified ?? ensuredUser?.attributes?.emailVerified)
      );
    } else if (typeof verifiedMail === 'boolean') {
      setIsEmailVerified(verifiedMail);
    } else if (ensuredUser?.attributes?.emailVerified !== undefined) {
      setIsEmailVerified(!!ensuredUser.attributes.emailVerified);
    }

    if (userId) {
      axios
        .get(`${apiBaseUrl()}/api/email-verification/${userId}`)
        .then(res => {
          if (res?.data) {
            if (res.data.emailVerified !== undefined && !isCurrentUser && typeof verifiedMail !== 'boolean') {
              setIsEmailVerified(!!res.data.emailVerified);
            }
            if (res.data.rating != null) {
              setSellerRating(res.data.rating);
            }
            if (res.data.ratingCount != null) {
              setSellerRatingCount(res.data.ratingCount);
            }
            if (res.data.lastSeen != null) {
              setSellerLastSeen(res.data.lastSeen);
            }
          }
        })
        .catch(() => {
          if (!isCurrentUser && typeof verifiedMail !== 'boolean' && ensuredUser?.attributes?.emailVerified === undefined) {
            setIsEmailVerified(false);
          }
        });
    }
  }, [userId, isCurrentUser, ensuredCurrentUser?.attributes?.emailVerified, verifiedMail]);

  const { displayName, bio } = ensuredUser.attributes.profile;
  const handleContactUserClick = () => {
    onContactUser(user);
  };

  const hasBio = !!bio;
  const classes = classNames(rootClassName || css.root, className);
  const linkClasses = classNames(css.links, {
    [css.withBioMissingAbove]: !hasBio,
  });

  const separator = <span className={css.linkSeparator}>•</span>;

  const chatIcon = (
    <svg className={css.chatBtnIcon} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zM6 9h12v2H6V9zm8 5H6v-2h8v2zm4-6H6V6h12v2z" />
    </svg>
  );

  const rating =
    sellerRating ||
    ensuredUser?.attributes?.profile?.publicData?.rating ||
    ensuredUser?.attributes?.profile?.metadata?.rating ||
    ensuredUser?.attributes?.profile?.publicData?.userRating;
  const ratingCount =
    sellerRatingCount ||
    ensuredUser?.attributes?.profile?.publicData?.ratingCount ||
    ensuredUser?.attributes?.profile?.metadata?.ratingCount ||
    ensuredUser?.attributes?.profile?.publicData?.userRatingCount;

  const renderRating = () => {
    const numRating = Number(rating);
    const numRatingCount = Number(ratingCount);
    if (isNaN(numRating) || numRating <= 0 || isNaN(numRatingCount) || numRatingCount <= 0) {
      return null;
    }
    const displayRating =
      numRating % 1 === 0 ? numRating.toString() : numRating.toFixed(1);
    return (
      <div className={css.ratingRow}>
        <svg className={css.starIcon} width="14" height="14" viewBox="0 0 24 24" fill="#fec000" stroke="#fec000">
          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"></polygon>
        </svg>
        <span className={css.ratingValue}>{displayRating}</span>
        <span className={css.ratingCount}>({numRatingCount})</span>
      </div>
    );
  };

  const contact = showContact ? (
    <InlineTextButton
      rootClassName={css.contact}
      onClick={handleContactUserClick}
      enforcePagePreloadFor="SignupPage"
    >
      <span className={css.chatIconWrapper}>
        <ChatIconNew className={css.chatBubbleIcon} />
      </span>
      <span><FormattedMessage id="UserCard.contactUserNew" defaultMessage="Chat with seller" /></span>
    </InlineTextButton>
  ) : null;

  const editProfile = (
    <NamedLink className={css.contact} name="ProfileSettingsPage">
      <FormattedMessage id="ListingPage.editProfileLink" />
    </NamedLink>
  );

  const links = mounted && isCurrentUser ? editProfile : contact;

  const profileUrl =
    ensuredUser?.attributes?.profile?.publicData?.profileUrl ||
    ensuredUser?.attributes?.profile?.publicData?.rawProfileUrl;
  const profileLinkProps = profileUrl
    ? { name: 'ProfilePageByUsername', params: { userName: profileUrl } }
    : ensuredUser.id
    ? { name: 'ProfilePage', params: { id: ensuredUser.id.uuid } }
    : { name: 'ProfileBasePage' };

  const lastActiveAt = sellerLastSeen || ensuredUser?.attributes?.profile?.metadata?.lastSeen;

  const renderActivityStatus = () => {
    if (!lastActiveAt) {
      return (
        <div className={css.activityStatus}>
          <span className={classNames(css.dot, css.dotGreen)} />
          <span className={css.activityLabel}>
            <FormattedMessage id="UserCard.activeToday" defaultMessage="Active today" />
          </span>
        </div>
      );
    }
    const timeDiffMs = new Date().getTime() - new Date(lastActiveAt).getTime();
    if (isNaN(timeDiffMs)) {
      return (
        <div className={css.activityStatus}>
          <span className={classNames(css.dot, css.dotGreen)} />
          <span className={css.activityLabel}>
            <FormattedMessage id="UserCard.activeToday" defaultMessage="Active today" />
          </span>
        </div>
      );
    }
    const timeDiffHours = timeDiffMs / (1000 * 60 * 60);
    const timeDiffDays = Math.floor(timeDiffHours / 24);

    if (timeDiffHours < 24) {
      return (
        <div className={css.activityStatus}>
          <span className={classNames(css.dot, css.dotGreen)} />
          <span className={css.activityLabel}>
            <FormattedMessage id="UserCard.activeToday" defaultMessage="Active today" />
          </span>
        </div>
      );
    } else if (timeDiffDays < 15) {
      const days = Math.max(1, timeDiffDays);
      return (
        <div className={css.activityStatus}>
          <span className={classNames(css.dot, css.dotAmber)} />
          <span className={css.activityLabel}>
            <FormattedMessage id="UserCard.activeDaysAgo" values={{ days }} />
          </span>
        </div>
      );
    } else if (timeDiffDays < 60) {
      const weeks = Math.floor(timeDiffDays / 7);
      return (
        <div className={css.activityStatus}>
          <span className={classNames(css.dot, css.dotGrey)} />
          <span className={css.activityLabel}>
            <FormattedMessage id="UserCard.activeWeeksAgo" values={{ weeks }} />
          </span>
        </div>
      );
    }

    return null;
  };

  const storeDisplayName =
    ensuredUser?.attributes?.profile?.publicData?.rawProfileUrl ||
    ensuredUser?.attributes?.profile?.publicData?.profileUrl ||
    storeName;

  const displayNameDisplay =
    storeDisplayName ||
    displayName ||
    ensuredUser?.attributes?.profile?.displayName ||
    'SELLER';

  return (
    <div className={classes}>
      <div className={css.sellerHeader}>
        <AvatarLarge className={css.avatar} user={user} />
        <div className={css.info}>
          <NamedLink className={css.storeProfileContainer} {...profileLinkProps}>
            <div className={css.nameRow}>
              <span className={css.storeText}>{displayNameDisplay.toUpperCase()}</span>
              {isEmailVerified ? <VerifySeller className={css.verifiedMail} /> : null}
            </div>
            {renderActivityStatus()}
            {renderRating()}
          </NamedLink>
        </div>
      </div>
      {links && (
        <div className={css.contactWrapper}>
          {links}
        </div>
      )}
    </div>
  );
};

export default UserCard;
